/**
 * Reimbursement-report grouping — pure functions (unit tested in
 * `tests/unit/report-groups.unit.spec.ts`). Rows roll up under their vendor
 * group the way the payments matrix does; the *expanded* set survives a reload
 * through localStorage (`use-expanded-groups.ts`) — groups start folded, so
 * remembering which ones the user opened is the smaller state to keep.
 */

import type { AmountsByCurrency, ReimbursementRow } from '../api/types';

/** Catch-all for payments whose subscription sits in no vendor group. */
export const UNGROUPED_LABEL = 'No group';

export interface ReportGroup {
  name: string;
  /** True for the catch-all bucket of subscriptions without a group. */
  isUngrouped: boolean;
  rows: ReimbursementRow[];
  /** Display name — the group's own, or the catch-all label. */
  label: string;
  /** Rubles the group actually spent in the window. */
  rubTotal: number;
  paidByCurrency: AmountsByCurrency;
  eventsCount: number;
}

/** `null` group name renders as the catch-all, everywhere (table, CSV, print). */
export function groupLabelOf(row: Pick<ReimbursementRow, 'groupName'>): string {
  return row.groupName ?? UNGROUPED_LABEL;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Buckets the report rows by vendor group, in the order the report returns
 * them (chronological) — the same ordering the flat table and the CSV used
 * before grouping, so all three agree line for line. A group only exists when
 * at least one payment landed in the period, so a zero-spend group can never
 * show up.
 */
export function groupReimbursementRows(rows: ReimbursementRow[]): ReportGroup[] {
  const order: string[] = [];
  const groups = new Map<string, ReportGroup>();

  for (const row of rows) {
    const name = groupLabelOf(row);
    let group = groups.get(name);
    if (!group) {
      group = {
        name,
        isUngrouped: row.groupName === null,
        rows: [],
        label: name,
        rubTotal: 0,
        paidByCurrency: {},
        eventsCount: 0,
      };
      groups.set(name, group);
      order.push(name);
    }
    group.rows.push(row);
    group.eventsCount += 1;
    group.paidByCurrency[row.paidCurrency] = round2((group.paidByCurrency[row.paidCurrency] ?? 0) + row.paidAmount);
    if (row.rubAmount !== null) group.rubTotal = round2(group.rubTotal + row.rubAmount);
  }

  return order.map((name) => groups.get(name) as ReportGroup);
}

/**
 * The expanded-group set serialized for localStorage. JSON, because a group
 * name may contain any character — a comma-joined string would not survive
 * "Acme, Inc.".
 */
export function encodeExpandedGroups(names: Iterable<string>): string {
  return JSON.stringify([...names]);
}

/** Tolerant read-back: anything unexpected simply means "nothing expanded". */
export function decodeExpandedGroups(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set();
  }
}
